import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../utils/supabase';
import { Resource, ResourceState } from '../../types/resources';

// Helper function to format dates
const formatDateForDB = (date: string | null | undefined) => {
  if (!date) return null;
  return new Date(date).toISOString();
};

// Async thunks
export const fetchResources = createAsyncThunk(
  'resources/fetchResources',
  async (_, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Resource[];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addResource = createAsyncThunk(
  'resources/addResource',
  async (resource: Omit<Resource, 'id' | 'created_at' | 'user_id'>, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const formattedResource = {
        ...resource,
        expiry_date: formatDateForDB(resource.expiry_date),
        user_id: user.id
      };

      const { data, error } = await supabase
        .from('resources')
        .insert([formattedResource])
        .select()
        .single();

      if (error) throw error;
      return data as Resource;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateResource = createAsyncThunk(
  'resources/updateResource',
  async ({ id, ...updates }: Partial<Resource> & { id: string }, thunkAPI) => {
    try {
      const formattedUpdates = {
        ...updates,
        expiry_date: formatDateForDB(updates.expiry_date),
      };

      const { data, error } = await supabase
        .from('resources')
        .update(formattedUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Resource;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteResource = createAsyncThunk(
  'resources/deleteResource',
  async (id: string, thunkAPI) => {
    try {
      const { error } = await supabase
        .from('resources')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const initialState: ResourceState = {
  resources: [],
  loading: false,
  error: null,
};

const resourceSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchResources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResources.fulfilled, (state, action) => {
        state.loading = false;
        state.resources = action.payload;
        state.error = null;
      })
      .addCase(fetchResources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addResource.fulfilled, (state, action) => {
        state.resources.unshift(action.payload);
      })
      .addCase(updateResource.fulfilled, (state, action) => {
        const index = state.resources.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.resources[index] = action.payload;
        }
      })
      .addCase(deleteResource.fulfilled, (state, action) => {
        state.resources = state.resources.filter(r => r.id !== action.payload);
      });
  },
});

export default resourceSlice.reducer;